import Header from '../../components/Header';
import Footer from '../../components/Footer';
import politicas from '../../data/politicas.json';
import { useEffect } from 'react';

const OutrasPoliticasPage = () => {
  useEffect(() => {
    const jsonLdData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Outras Políticas - Target Software',
      description:
        'Conheça nossas políticas corporativas e diretrizes que guiam nossas práticas e compromissos na Target Software.',
      url: 'https://targetsoftware.com.br/outras-politicas',
      mainEntity: {
        '@type': 'ItemList',
        name: 'Políticas Corporativas',
        description:
          'Lista completa das políticas e diretrizes corporativas da Target Software',
        numberOfItems: politicas.length,
        itemListElement: politicas.map((politica, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'WebPage',
            name: politica.name,
            description: politica.description,
            url: `https://targetsoftware.com.br/politicas/${politica.slug}`,
          },
        })),
      },
      publisher: {
        '@type': 'Organization',
        name: 'Target Software',
        description:
          'Empresa especializada em soluções SaaS para incentivos comerciais, gestão de parceiros e business intelligence.',
        url: 'https://targetsoftware.com.br',
        logo: 'https://targetsoftware.com.br/images/logos/logo-target.png',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header startInDarkMode={false} />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#F6F6F6] to-[#E3E3E3] overflow-hidden">
        <div className="absolute inset-0">
          {/* Enhanced Floating Elements */}
          <div className="absolute top-20 left-10 w-2 h-2 bg-[#7d2128] rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-gray-400 rounded-full animate-ping opacity-40"></div>

          {/* New Horizontal Moving Elements */}
          <div className="absolute top-16 left-0 w-4 h-4 bg-[#7d2128]/20 animate-move-right opacity-40"></div>
          <div className="absolute top-32 left-0 w-2 h-2 bg-gray-500/30 animate-move-right-slow opacity-50" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-48 left-0 w-3 h-3 bg-[#7d2128]/30 animate-move-right-fast opacity-60" style={{ animationDelay: '2s' }}></div>

          {/* New Elements Moving from Right */}
          <div className="absolute top-24 right-0 w-4 h-4 bg-gray-600/25 animate-move-left opacity-45"></div>
          <div className="absolute top-40 right-0 w-2 h-2 bg-[#7d2128]/35 animate-move-left-fast opacity-55" style={{ animationDelay: '0.8s' }}></div>
          <div className="absolute bottom-28 right-0 w-3 h-3 bg-gray-500/30 animate-move-left-slow opacity-40" style={{ animationDelay: '1.2s' }}></div>

          {/* Enhanced Vertical Moving Elements */}
          <div className="absolute top-0 left-16 w-6 h-6 bg-[#7d2128]/15 rounded-full animate-move-down opacity-40"></div>
          <div className="absolute top-0 left-32 w-4 h-4 bg-gray-500/20 rounded-full animate-move-down-slow opacity-50" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-0 left-48 w-3 h-3 bg-[#7d2128]/25 rounded-full animate-move-down-fast opacity-60" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-0 right-16 w-5 h-5 bg-gray-400/18 rounded-full animate-move-down opacity-35" style={{ animationDelay: '1.5s' }}></div>

          {/* New Elements Moving Up */}
          <div className="absolute bottom-0 left-24 w-5 h-5 bg-gray-500/20 rounded-full animate-move-up opacity-40"></div>
          <div className="absolute bottom-0 left-40 w-3 h-3 bg-[#7d2128]/28 rounded-full animate-move-up-slow opacity-50" style={{ animationDelay: '0.7s' }}></div>
          <div className="absolute bottom-0 right-24 w-4 h-4 bg-gray-400/25 rounded-full animate-move-up-fast opacity-45" style={{ animationDelay: '1.3s' }}></div>

          {/* Rotating Elements */}
          <div className="absolute top-1/4 left-12 w-8 h-8 bg-[#7d2128]/10 animate-rotate-slow opacity-30"></div>
          <div className="absolute top-3/4 right-12 w-6 h-6 bg-gray-500/15 animate-rotate-reverse opacity-40" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-20 w-4 h-4 bg-[#7d2128]/20 animate-rotate-fast opacity-50" style={{ animationDelay: '0.5s' }}></div>

          {/* Floating Geometric Shapes */}
          <div className="absolute top-32 left-1/4 w-3 h-3 bg-[#7d2128]/25 rotate-45 animate-float opacity-45"></div>
          <div className="absolute bottom-32 right-1/4 w-4 h-4 bg-gray-500/20 rotate-45 animate-float-slow opacity-40" style={{ animationDelay: '0.8s' }}></div>

          {/* Additional Circuit-like Elements */}
          <div className="absolute top-20 right-1/3 w-6 h-1 bg-[#7d2128]/30 animate-pulse opacity-40" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-8 h-1 bg-gray-500/25 animate-pulse opacity-35" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-8 w-4 h-1 bg-[#7d2128]/40 animate-pulse opacity-50" style={{ animationDelay: '1.5s' }}></div>

          {/* Corner Elements */}
          <div className="absolute top-4 left-4 w-2 h-2 bg-[#7d2128]/20 animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-4 right-4 w-3 h-3 bg-gray-500/15 animate-bounce opacity-40" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 mt-9">
              Outras <span className="text-[#7d2128]">Políticas</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Conheça nossas políticas corporativas e diretrizes <br />que guiam nossas práticas e compromissos.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {politicas.map((politica) => (
              <div key={politica.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#7d2128]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-2xl text-[#7d2128]"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-[#7d2128] transition-colors duration-200">
                      {politica.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {politica.description}
                    </p>
                    <a
                      href={`/politicas/${politica.slug}`}
                      className="inline-flex items-center text-[#7d2128] hover:text-[#5a161c] font-medium text-sm transition-colors duration-200"
                    >
                      <span>Ler política completa</span>
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 mb-4">
                <strong>Precisa de mais informações?</strong>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Entre em contato conosco para esclarecer dúvidas sobre nossas políticas ou solicitar acesso a documentos específicos.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OutrasPoliticasPage;